/**
 * Automatic technical reading of the 5-minute candles: trend lines, the chart pattern they
 * make, candlestick patterns and a volume profile. Pure functions over the bars — the
 * workspace draws what they return.
 */

export interface TaBar {
    time: number;
    open: number;
    high: number;
    low: number;
    close: number;
    volume?: number;
}

export interface TaPivot {
    i: number;
    time: number;
    price: number;
}

export interface TaLine {
    kind: "resistance" | "support";
    a: TaPivot;
    b: TaPivot;
    /** price change per bar */
    slope: number;
    /** the line's value at the last bar */
    last: number;
}

export interface TaCandleMark {
    time: number;
    code: string;
    name: string;
    bullish: boolean;
}

export interface TaProfileBin {
    lo: number;
    hi: number;
    volume: number;
}

export interface TaResult {
    trend: "up" | "down" | "sideways";
    lines: TaLine[];
    pattern: string | null;
    candles: TaCandleMark[];
    profile: { bins: TaProfileBin[]; poc: number; vah: number; val: number } | null;
}

/** Swing highs and lows: a bar that is the extreme of `w` bars either side of it. */
function pivots(bars: TaBar[], w: number): { highs: TaPivot[]; lows: TaPivot[] } {
    const highs: TaPivot[] = [];
    const lows: TaPivot[] = [];
    for (let i = w; i < bars.length - w; i++) {
        let hi = true;
        let lo = true;
        for (let k = i - w; k <= i + w; k++) {
            if (k === i) continue;
            if (bars[k].high >= bars[i].high) hi = false;
            if (bars[k].low <= bars[i].low) lo = false;
        }
        if (hi) highs.push({ i, time: bars[i].time, price: bars[i].high });
        if (lo) lows.push({ i, time: bars[i].time, price: bars[i].low });
    }
    return { highs, lows };
}

/**
 * The line through two recent swings that the bars after the first one respect: for
 * resistance, no later high closes above it by more than a hair. Searches the last few
 * swings for the pair that holds and spans the most bars.
 */
function fitLine(bars: TaBar[], pts: TaPivot[], kind: "resistance" | "support", tol: number): TaLine | null {
    const recent = pts.slice(-6);
    let best: TaLine | null = null;
    for (let x = 0; x < recent.length - 1; x++) {
        for (let y = x + 1; y < recent.length; y++) {
            const a = recent[x];
            const b = recent[y];
            if (b.i - a.i < 6) continue;
            const slope = (b.price - a.price) / (b.i - a.i);
            let ok = true;
            for (let k = a.i; k < bars.length; k++) {
                const v = a.price + slope * (k - a.i);
                if (kind === "resistance" ? bars[k].close > v + tol : bars[k].close < v - tol) {
                    ok = false;
                    break;
                }
            }
            if (!ok) continue;
            const line: TaLine = { kind, a, b, slope, last: a.price + slope * (bars.length - 1 - a.i) };
            if (!best || b.i - a.i > best.b.i - best.a.i) best = line;
        }
    }
    return best;
}

function classify(bars: TaBar[], res: TaLine | null, sup: TaLine | null, highs: TaPivot[], lows: TaPivot[], unit: number): string | null {
    // double top / bottom: two swings at the same price with a real pullback between them
    const near = (p: number, q: number) => Math.abs(p - q) <= unit * 0.6;
    if (highs.length >= 2) {
        const [h1, h2] = highs.slice(-2);
        const dip = Math.min(...bars.slice(h1.i, h2.i + 1).map((b) => b.low));
        if (near(h1.price, h2.price) && h2.i - h1.i >= 8 && h1.price - dip >= unit * 2.5 && h2.i >= bars.length - 40) return "Double Top";
    }
    if (lows.length >= 2) {
        const [l1, l2] = lows.slice(-2);
        const pop = Math.max(...bars.slice(l1.i, l2.i + 1).map((b) => b.high));
        if (near(l1.price, l2.price) && l2.i - l1.i >= 8 && pop - l1.price >= unit * 2.5 && l2.i >= bars.length - 40) return "Double Bottom";
    }
    if (!res || !sup) return null;
    const flat = unit * 0.035; // slope per bar under which a line counts as flat
    const up = (s: number) => s > flat;
    const dn = (s: number) => s < -flat;
    const converging = res.last - sup.last < (res.a.price - (sup.a.price + sup.slope * (res.a.i - sup.a.i))) * 0.75;
    if (!up(res.slope) && !dn(res.slope) && !up(sup.slope) && !dn(sup.slope)) return "Range";
    if (!up(res.slope) && !dn(res.slope) && up(sup.slope)) return "Ascending Triangle";
    if (dn(res.slope) && !up(sup.slope) && !dn(sup.slope)) return "Descending Triangle";
    if (dn(res.slope) && up(sup.slope)) return "Symmetrical Triangle";
    if (up(res.slope) && up(sup.slope)) return converging ? "Rising Wedge" : "Ascending Channel";
    if (dn(res.slope) && dn(sup.slope)) return converging ? "Falling Wedge" : "Descending Channel";
    return "Broadening";
}

/** Classic one- to three-bar candlestick patterns, each read against the move before it. */
function candlePatterns(bars: TaBar[], unit: number, lookback = 78): TaCandleMark[] {
    const out: TaCandleMark[] = [];
    const body = (b: TaBar) => Math.abs(b.close - b.open);
    const range = (b: TaBar) => b.high - b.low;
    const upper = (b: TaBar) => b.high - Math.max(b.open, b.close);
    const lower = (b: TaBar) => Math.min(b.open, b.close) - b.low;
    const bull = (b: TaBar) => b.close > b.open;
    const start = Math.max(6, bars.length - lookback);
    for (let i = start; i < bars.length; i++) {
        const c = bars[i];
        const p = bars[i - 1];
        const pp = bars[i - 2];
        if (range(c) < unit * 0.5) continue; // too small a bar to mean anything
        const before = c.close - bars[i - 6].close; // the move the pattern would be reversing
        const fell = before < -unit * 1.2;
        const rose = before > unit * 1.2;
        let m: Omit<TaCandleMark, "time"> | null = null;
        if (fell && bull(c) && !bull(p) && c.close >= p.open && c.open <= p.close && body(c) > body(p) * 1.1) m = { code: "BE", name: "Bullish Engulfing", bullish: true };
        else if (rose && !bull(c) && bull(p) && c.close <= p.open && c.open >= p.close && body(c) > body(p) * 1.1) m = { code: "SE", name: "Bearish Engulfing", bullish: false };
        else if (fell && lower(c) >= body(c) * 2 && upper(c) <= body(c) * 0.6 && body(c) > 0) m = { code: "H", name: "Hammer", bullish: true };
        else if (rose && upper(c) >= body(c) * 2 && lower(c) <= body(c) * 0.6 && body(c) > 0) m = { code: "SS", name: "Shooting Star", bullish: false };
        else if (fell && !bull(pp) && body(pp) > unit && body(p) < body(pp) * 0.35 && bull(c) && c.close > (pp.open + pp.close) / 2) m = { code: "MS", name: "Morning Star", bullish: true };
        else if (rose && bull(pp) && body(pp) > unit && body(p) < body(pp) * 0.35 && !bull(c) && c.close < (pp.open + pp.close) / 2) m = { code: "ES", name: "Evening Star", bullish: false };
        else if ((fell || rose) && body(c) <= range(c) * 0.08 && range(c) > unit) m = { code: "D", name: "Doji", bullish: fell };
        if (m) out.push({ time: c.time, ...m });
    }
    // a chart with a marker on every other bar reads as noise: keep the latest few
    return out.slice(-8);
}

/** Volume traded at each price over the bars shown; a bar's volume is spread over its range. */
function volumeProfile(bars: TaBar[], binsWanted = 36): TaResult["profile"] {
    const withVol = bars.filter((b) => (b.volume || 0) > 0);
    if (withVol.length < 20) return null;
    const lo = Math.min(...withVol.map((b) => b.low));
    const hi = Math.max(...withVol.map((b) => b.high));
    if (!(hi > lo)) return null;
    const step = (hi - lo) / binsWanted;
    const bins: TaProfileBin[] = Array.from({ length: binsWanted }, (_, k) => ({ lo: lo + k * step, hi: lo + (k + 1) * step, volume: 0 }));
    for (const b of withVol) {
        const first = Math.max(0, Math.min(binsWanted - 1, Math.floor((b.low - lo) / step)));
        const last = Math.max(0, Math.min(binsWanted - 1, Math.floor((b.high - lo) / step)));
        const share = (b.volume as number) / (last - first + 1);
        for (let k = first; k <= last; k++) bins[k].volume += share;
    }
    let pocIdx = 0;
    bins.forEach((b, k) => {
        if (b.volume > bins[pocIdx].volume) pocIdx = k;
    });
    // value area: grow out from the point of control until 70% of the volume is inside
    const total = bins.reduce((s, b) => s + b.volume, 0);
    let a = pocIdx;
    let z = pocIdx;
    let inside = bins[pocIdx].volume;
    while (inside < total * 0.7 && (a > 0 || z < binsWanted - 1)) {
        const below = a > 0 ? bins[a - 1].volume : -1;
        const above = z < binsWanted - 1 ? bins[z + 1].volume : -1;
        if (above >= below) inside += bins[++z].volume;
        else inside += bins[--a].volume;
    }
    const mid = (b: TaProfileBin) => (b.lo + b.hi) / 2;
    return { bins, poc: mid(bins[pocIdx]), vah: bins[z].hi, val: bins[a].lo };
}

export function analyse(bars: TaBar[]): TaResult {
    const empty: TaResult = { trend: "sideways", lines: [], pattern: null, candles: [], profile: null };
    if (bars.length < 40) return empty;
    const view = bars.slice(-156); // the last two sessions: what the eye is on
    // "unit" = a typical bar's range, so every threshold scales with the stock
    const ranges = view.map((b) => b.high - b.low).sort((x, y) => x - y);
    const unit = ranges[Math.floor(ranges.length / 2)] || view[view.length - 1].close * 0.001;
    const { highs, lows } = pivots(view, 4);
    const res = fitLine(view, highs, "resistance", unit * 0.5);
    const sup = fitLine(view, lows, "support", unit * 0.5);
    const lines = [res, sup].filter(Boolean) as TaLine[];
    const first = view[0].close;
    const last = view[view.length - 1].close;
    const net = (last - first) / unit;
    const slopeSum = (res ? res.slope : 0) + (sup ? sup.slope : 0);
    const trend: TaResult["trend"] = net > 6 && slopeSum >= 0 ? "up" : net < -6 && slopeSum <= 0 ? "down" : "sideways";
    return {
        trend,
        lines,
        pattern: classify(view, res, sup, highs, lows, unit),
        candles: candlePatterns(view, unit),
        profile: volumeProfile(view),
    };
}
